import {afterNextRender, afterRenderEffect, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, signal, viewChild, ViewEncapsulation} from '@angular/core';
import {STRINGS} from './assistant.constants';
import {AssistantMessageComponent} from './assistant-message.component';
import {ChatStore} from './chat-store';
import {IconComponent} from './icon.component';
import {UserMessageComponent} from './messages.components';

/** Auto-scroll stays on only while the user is within this many pixels of the bottom. */
export const PIN_THRESHOLD_PX = 64;

export function isNearBottom(element: Pick<HTMLElement, 'scrollTop' | 'scrollHeight' | 'clientHeight'>, threshold = PIN_THRESHOLD_PX): boolean {
  return element.scrollHeight - element.scrollTop - element.clientHeight <= threshold;
}

/**
 * Message list with smart auto-scroll: it follows new text only while the user is at the
 * bottom. Scrolling up pauses it and shows "Jump to latest". The live regions announce finished
 * answers and errors (polite) and the status line once per turn.
 */
@Component({
  selector: 'app-aa-chat-log',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Global styles scoped under .aa-root, like the panel.
  encapsulation: ViewEncapsulation.None,
  styleUrl: './chat-messages.scss',
  imports: [AssistantMessageComponent, IconComponent, UserMessageComponent],
  host: {'class': 'aa-log-wrap'},
  template: `
    <div #log class="aa-log" tabindex="0" role="region" [attr.aria-label]="strings.conversation" (scroll)="onScroll()">
      @for (turn of store.turns(); track turn.id) {
        @if (turn.role === 'user') {
          <app-aa-user-message [text]="turn.text"/>
        } @else {
          <app-aa-assistant-message [turn]="turn" [isLatest]="turn.id === store.latestAssistantId()"/>
        }
      }
    </div>
    @if (!pinned()) {
      <button class="aa-jump" type="button" [attr.aria-label]="strings.jumpToLatest" [title]="strings.jumpToLatest" (click)="jumpToLatest()">
        <app-aa-icon name="arrow-down"/>
      </button>
    }
    <div class="aa-visually-hidden" aria-live="polite" aria-atomic="true">{{ store.announcement() }}</div>
    <div class="aa-visually-hidden" role="status">{{ store.statusAnnouncement() }}</div>
  `,
})
export class ChatLogComponent {
  protected readonly store = inject(ChatStore);
  protected readonly strings = STRINGS;
  protected readonly pinned = signal(true);
  private readonly log = viewChild.required<ElementRef<HTMLElement>>('log');
  private readonly destroyRef = inject(DestroyRef);
  private lastTurnCount = 0;

  constructor() {
    // The composer growing (or the panel resizing) shrinks the log: stay at the bottom if pinned.
    afterNextRender(() => {
      const element = this.log().nativeElement;
      const observer = new ResizeObserver(() => {
        if (this.pinned()) {
          element.scrollTop = element.scrollHeight;
        }
      });
      observer.observe(element);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });

    afterRenderEffect(() => {
      const turns = this.store.turns();
      const last = turns[turns.length - 1];
      // A message the user just sent always brings the view back down.
      if (turns.length > this.lastTurnCount && turns[turns.length - 2]?.role === 'user' && last?.role === 'assistant' && last.state === 'pending') {
        this.pinned.set(true);
      }
      this.lastTurnCount = turns.length;

      if (this.pinned()) {
        const element = this.log().nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    });
  }

  protected onScroll(): void {
    this.pinned.set(isNearBottom(this.log().nativeElement));
  }

  protected jumpToLatest(): void {
    const element = this.log().nativeElement;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    element.scrollTo({top: element.scrollHeight, behavior: reduceMotion ? 'auto' : 'smooth'});
    this.pinned.set(true);
    // The button disappears; keep focus in the conversation instead of losing it to the page.
    element.focus();
  }
}
